import { Box, ButtonBase, Typography } from '@mui/material';
import { Baby } from 'lucide-react';
import { useId, type ReactElement } from 'react';
import { Link } from 'react-router';

import { Badge, Icon, visuallyHidden } from './kit';
import { targetPath, type ActiveNavEntry, type RenderedNavEntry, type RenderedNavSection } from './navigation';
import { EnvironmentBadge } from './shell/environment';
import { useNavBadges } from './shell/navBadges';

/**
 * Barre laterale (maquette « BO/Barre latérale ») : marque, bandeau
 * d'environnement, puis les rubriques **rendues** du registre.
 *
 * Elle ne decide de rien : les rubriques et leurs entrees viennent de
 * `visibleSections`, l'entree courante de `activeEntryFor`. Le meme contenu sert
 * la colonne fixe des grands ecrans et le tiroir des petits.
 */

const Brand = (): ReactElement => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 0.75, py: 0.5 }}>
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        bgcolor: 'tokens.primary',
        color: 'tokens.textOnPrimary',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Icon icon={Baby} size={20} />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: '22px' }}>MiaMiam</Typography>
      <Typography sx={{ fontSize: 12, lineHeight: '16px', color: 'tokens.textMuted' }}>Administration</Typography>
    </Box>
  </Box>
);

const EntryRow = ({
  entry,
  current,
  count,
  onNavigate,
}: {
  readonly entry: RenderedNavEntry;
  readonly current: boolean;
  readonly count: number | undefined;
  readonly onNavigate: (() => void) | undefined;
}): ReactElement => (
  <li>
    <ButtonBase
      component={Link}
      to={targetPath(entry.target)}
      aria-current={current ? 'page' : undefined}
      onClick={onNavigate}
      sx={{
        width: '100%',
        minHeight: 40,
        px: 1.25,
        gap: 1.25,
        justifyContent: 'flex-start',
        borderRadius: '10px',
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: current ? 700 : 600,
        // `accentPinkStrong` et non `primary` : l'encre rose qui tient 4,5:1 sur
        // l'aplat `primarySoft` dans les deux modes.
        color: current ? 'tokens.accentPinkStrong' : 'tokens.textStrong',
        bgcolor: current ? 'tokens.primarySoft' : 'transparent',
        '&:hover': { bgcolor: current ? 'tokens.primarySoft' : 'tokens.surfaceMuted' },
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', color: current ? 'inherit' : 'tokens.textMuted' }}>
        <Icon icon={entry.icon} size={18} />
      </Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0, textAlign: 'left', overflowWrap: 'anywhere' }}>
        {entry.label}
      </Box>
      {/* Zero se lit « rien a traiter » : pas de pastille, comme dans la maquette. */}
      {count === undefined || count === 0 ? null : (
        <>
          <Badge label={count} tone="brand" />
          {/* Le nombre seul ne dit pas ce qu'il compte a qui ne le voit pas. */}
          <Box component="span" sx={visuallyHidden}>
            à traiter
          </Box>
        </>
      )}
    </ButtonBase>
  </li>
);

const Section = ({
  section,
  active,
  onNavigate,
}: {
  readonly section: RenderedNavSection;
  readonly active: ActiveNavEntry | null;
  readonly onNavigate: (() => void) | undefined;
}): ReactElement => {
  const titleId = useId();
  const { counts } = useNavBadges();

  return (
    <Box>
      <Typography
        id={titleId}
        sx={{ px: 1.25, pb: 0.75, fontSize: 11, fontWeight: 700, lineHeight: '16px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'tokens.textMuted' }}
      >
        {section.label}
      </Typography>
      <Box component="ul" aria-labelledby={titleId} sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.25 }}>
        {section.entries.map((entry) => (
          <EntryRow
            key={entry.id}
            entry={entry}
            current={active?.entry.id === entry.id}
            count={counts[entry.id]}
            onNavigate={onNavigate}
          />
        ))}
      </Box>
    </Box>
  );
};

export const AdminMenu = ({
  sections,
  active,
  onNavigate,
}: {
  readonly sections: readonly RenderedNavSection[];
  readonly active: ActiveNavEntry | null;
  /** Ferme le tiroir apres un choix, sur petit ecran. */
  readonly onNavigate?: () => void;
}): ReactElement => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: 2.25 }}>
    <Brand />
    <Box sx={{ px: 0.75 }}>
      <EnvironmentBadge />
    </Box>
    <Box component="nav" aria-label="Navigation principale" sx={{ display: 'grid', gap: 1.75 }}>
      {sections.map((section) => (
        <Section key={section.id} section={section} active={active} onNavigate={onNavigate} />
      ))}
    </Box>
  </Box>
);
